import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { motion, useReducedMotion } from 'framer-motion'
import type { ExpertResponse as ExpertResponseType, GateStopped, ResponseMode } from '@/types/expert'
import { ModeBadge } from '@/components/ui/Badge'
import { CitationChip } from './CitationChip'
import { CodeBlock } from './CodeBlock'
import { RatingWidget } from './RatingWidget'
import { AnswerExplanation } from '@/components/chat/AnswerExplanation'
import { useReplyStore } from '@/stores/replyStore'
import { splitContentByCitations } from '@/utils/parseCitations'
const LARGE_ANSWER_THRESHOLD = 4000
import { cn } from '@/utils/cn'
import { ARC_MOTION } from '@/design-system/motion'
import { buildResponseMarkdown, buildMarkdownFilename, downloadMarkdown } from '@/utils/exportMarkdown'

// ============================================================
// ReasoningPanel — gate-by-gate decision breakdown
// ============================================================

const GATES: {
  num: 1 | 2 | 3 | 4 | 5
  name: string
  description: string
  passColor: string
  stopColor: string
}[] = [
  {
    num: 1,
    name: 'Clarity',
    description: 'Does the expert have enough information to answer? If not, clarifying questions are raised (ASK mode).',
    passColor: 'text-mode-advise',
    stopColor: 'text-mode-ask',
  },
  {
    num: 2,
    name: 'Coverage',
    description: 'Is the question covered by the expert\'s training material? Out-of-scope questions are refused here.',
    passColor: 'text-mode-advise',
    stopColor: 'text-mode-refuse',
  },
  {
    num: 3,
    name: 'Charter',
    description: 'Does the answer comply with the expert\'s reasoning charter and domain rules? Violations trigger WARN or PUSH_BACK.',
    passColor: 'text-mode-advise',
    stopColor: 'text-mode-warn',
  },
  {
    num: 4,
    name: 'Complexity',
    description: 'Is the proposed solution appropriately scoped? Over-engineered answers are pushed back for simplification.',
    passColor: 'text-mode-advise',
    stopColor: 'text-mode-pushback',
  },
  {
    num: 5,
    name: 'China Wall',
    description: 'Can the answer be fully grounded in cited training material? If not, the expert refuses rather than hallucinate.',
    passColor: 'text-mode-advise',
    stopColor: 'text-mode-refuse',
  },
]

function gateStatus(
  gateNum: 1 | 2 | 3 | 4 | 5,
  gateStopped: GateStopped
): 'passed' | 'stopped' | 'skipped' {
  // gateStopped=0 means all gates passed (answer generated after Gate 5)
  if (gateStopped === 0) return 'passed'
  // gateStopped=-1 is the structure-permission sentinel — gates not run
  if (gateStopped === -1) return 'skipped'
  if (gateNum < gateStopped) return 'passed'
  if (gateNum === gateStopped) return 'stopped'
  return 'skipped'
}

function ReasoningPanel({
  gateStopped,
  mode,
  confidence,
}: {
  gateStopped: GateStopped
  mode: ResponseMode
  confidence: number
}) {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="mt-3 overflow-hidden rounded-md border border-brand/30 bg-gradient-to-br from-brand/5 to-surface-overlay/60 p-4 text-xs shadow-sm"
    >
      <div className="mb-3 flex items-center gap-2">
        <span className="text-lg">{'\ud83e\udde0'}</span>
        <p className="font-semibold text-text-primary">Decision Trace</p>
        <span className="ml-auto rounded-full bg-brand/20 px-2 py-0.5 text-[10px] font-medium text-brand">AI Reasoning</span>
      </div>

      {/* Structure-permission sentinel — special case */}
      {gateStopped === -1 && (
        <div className="mb-3 rounded-md border border-mode-ask/30 bg-mode-ask/5 p-3">
          <div className="flex items-start gap-2">
            <span className="text-base">{'\u2753'}</span>
            <div>
              <p className="font-medium text-mode-ask">Structure Permission Question</p>
              <p className="mt-1 text-text-secondary">
                The expert asked whether to use a structured template or plain prose before answering. Gates were not evaluated.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Gate pipeline */}
      {gateStopped !== -1 && (
        <div className="space-y-2">
          {GATES.map((gate) => {
            const status = gateStatus(gate.num, gateStopped)
            return (
              <div
                key={gate.num}
                className={cn(
                  'flex items-start gap-2 rounded px-2 py-1.5',
                  status === 'stopped' && 'border border-surface-border bg-surface-base/60',
                  status === 'skipped' && 'opacity-40'
                )}
              >
                {/* Status icon */}
                <span
                  className={cn(
                    'mt-0.5 shrink-0 font-bold',
                    status === 'passed' && gate.passColor,
                    status === 'stopped' && gate.stopColor,
                    status === 'skipped' && 'text-text-disabled'
                  )}
                >
                  {status === 'passed' && '\u2713'}
                  {status === 'stopped' && '\u25cf'}
                  {status === 'skipped' && '\u25cb'}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-text-primary">
                      Gate {gate.num} — {gate.name}
                    </span>
                    {status === 'stopped' && (
                      <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide', gate.stopColor, 'bg-current/10')}>
                        stopped here
                      </span>
                    )}
                    {status === 'passed' && (
                      <span className="text-[10px] text-text-disabled">passed</span>
                    )}
                    {status === 'skipped' && (
                      <span className="text-[10px] text-text-disabled">not reached</span>
                    )}
                  </div>
                  {status === 'stopped' && (
                    <p className="mt-1 text-text-secondary leading-relaxed">{gate.description}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Summary row */}
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-surface-border/50 bg-surface-base/40 px-3 py-2 text-text-disabled">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider">Mode:</span>
          <span className="font-medium text-text-secondary">{mode}</span>
        </div>
        <div className="h-3 w-px bg-surface-border" />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wider">Confidence:</span>
          <span className="font-medium text-text-secondary">{Math.round(confidence * 100)}%</span>
        </div>
        {gateStopped === 0 && (
          <>
            <div className="h-3 w-px bg-surface-border" />
            <span className="flex items-center gap-1 text-mode-advise">
              <span>{'\u2713'}</span>
              <span className="font-medium">All 5 gates passed</span>
            </span>
          </>
        )}
      </div>
    </motion.div>
  )
}

/**
 * ARC-51 §9 (docs/ARC51_UI_CONTRACT.md): mode -> left-border glow
 * color, reusing the EXISTING product-semantic mode colors (§2's
 * rule: mode colors encode Gate/China Wall output, never repurposed
 * as decoration elsewhere). Not a new color system - just a new place
 * these already-meaningful colors get used.
 */
const MODE_BORDER_GLOW: Record<ExpertResponseType['mode'], string> = {
  ADVISE: 'border-l-mode-advise shadow-[-4px_0_16px_-8px_var(--color-advise)]',
  ASK: 'border-l-mode-ask shadow-[-4px_0_16px_-8px_var(--color-ask)]',
  WARN: 'border-l-mode-warn shadow-[-4px_0_16px_-8px_var(--color-warn)]',
  PUSH_BACK: 'border-l-mode-pushback shadow-[-4px_0_16px_-8px_var(--color-pushback)]',
  REFUSE: 'border-l-mode-refuse shadow-[-4px_0_16px_-8px_var(--color-refuse)]',
}

/**
 * Source: FRONTEND_SYSTEM_DESIGN.md section 9 ("This is the core value
 * proposition of AI Avengers. Every design decision here affects
 * trust and usability.") and section 10's Chat Page wireframe (mode
 * badge + confidence %, markdown content with inline citations, code
 * blocks, citation list, rating, collapsible "Why did I say this?").
 *
 * `persistedMessageId` prop design decision: see RatingWidget.tsx's
 * header comment - rating only applies to already-saved messages.
 * This component is used for BOTH live-streaming ExpertResponse
 * objects (no messageId yet) and persisted Message rows rendered from
 * getMessages() (has an id) - passing persistedMessageId=undefined for
 * the former simply hides the rating UI until the turn is saved and
 * the page's message list is refetched. This mirrors exactly how the
 * ASK-mode "Why did I say this?" collapsible section only makes sense
 * once there's a decision to explain, not mid-stream.
 */
export interface ExpertResponseProps {
  response: ExpertResponseType
  persistedMessageId?: string
  isStreaming?: boolean
  /**
   * CT-D5: required to key replyStore (CT-D3). Optional in the type
   * only because some historical call sites may not have it yet —
   * the reply button silently does nothing without it, never throws.
   */
  chatId?: string
}

export function ExpertResponse({ response, persistedMessageId, isStreaming, chatId }: ExpertResponseProps) {
  const [showReasoning, setShowReasoning] = useState(false)
  const [copied, setCopied] = useState(false)
  const [isExpanded, setIsExpanded] = useState(false)
  const [showExplanation, setShowExplanation] = useState(false)
  const reduceMotion = useReducedMotion()
  const setReplyTarget = useReplyStore((s) => s.setReplyTarget)

  // Feature #13: Calculate word count to determine if response is long
  const wordCount = response.content.split(/\s+/).filter(word => word.length > 0).length
  // WHY the char threshold is used again: LARGE_ANSWER_THRESHOLD used to guard the
  // (now removed) virtualised branch. Word count alone misses long code blocks /
  // tables, which are few "words" but heavy for the DOM. Either signal collapses.
  const isLongResponse = wordCount > 500 || response.content.length > LARGE_ANSWER_THRESHOLD
  const shouldTruncate = isLongResponse && !isExpanded

  const handleDownloadMarkdown = () => {
    const markdown = buildResponseMarkdown(response)
    const filename = buildMarkdownFilename(response.expertName ?? 'response')
    downloadMarkdown(markdown, filename)
  }

  function handleCopyResponse() {
    const text =
      response.templateSections && response.templateSections.length > 0
        ? response.templateSections.map((s) => s.label + ':\n' + s.content).join('\n\n')
        : response.content
    // WHY the fallback: navigator.clipboard only exists in a secure context
    // (https or localhost). On a plain-http deployment the button silently did
    // nothing, which is the bug this fixes. The textarea + execCommand path
    // works there, so the button copies in both environments.
    const markCopied = () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
    const legacyCopy = () => {
      try {
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(area)
        if (ok) markCopied()
        else console.warn('copy failed: clipboard is not available in this context')
      } catch (err) {
        console.warn('copy failed', err)
      }
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(markCopied).catch(legacyCopy)
    } else {
      legacyCopy()
    }
  }

  function handleReplyClick() {
    if (!chatId || !persistedMessageId) return
    const preview = response.content.length > 80 ? `${response.content.slice(0, 80)}…` : response.content
    setReplyTarget(chatId, {
      messageId: persistedMessageId,
      preview: preview || '(structured answer)',
      expertId: response.expertId,
      expertName: response.expertName,
    })
  }

  // WHY guard on response.error before rendering the normal body: a
  // partially-failed expert (one expert's goroutine errored while
  // others succeeded, per AI_AVENGERS_SYSTEM_ARCHITECTURE.md section
  // 4.1's "Failure behavior") still has expertId/expertName but no
  // real content/mode to render meaningfully - rendering the error
  // message here instead of an empty/garbled response body.
  if (response.error) {
    return (
      <div className="rounded-lg border border-mode-refuse/30 bg-mode-refuse/5 p-4">
        <p className="text-sm font-medium text-text-primary">{response.expertName}</p>
        <p className="mt-1 text-sm text-mode-refuse">Failed to respond: {response.error}</p>
      </div>
    )
  }

  const segments = splitContentByCitations(response.content, response.citations)

  // Build chunkId -> 1-based index map so inline citations match the
  // bottom citations list. Both use the same numbering.
  // 2026-09-08 RCA: `?? []` guard - see splitContentByCitations' own
  // doc comment (parseCitations.ts) for the full incident this
  // defends against (a nil backend slice marshaling as JSON null).
  const citationIndexMap = new Map(
    (response.citations ?? []).map((c, i) => [c.chunkId, i + 1])
  )

  return (
    <motion.div
      initial={reduceMotion ? undefined : { opacity: 0, y: 8 }}
      animate={{ opacity: isStreaming ? 0.8 : 1, y: 0 }}
      transition={{ duration: ARC_MOTION.panel, ease: ARC_MOTION.ease }}
      className={cn(
        'rounded-lg border-l-4 border-y border-r border-glass-border bg-surface-raised/80 p-4 backdrop-blur-xl',
        MODE_BORDER_GLOW[response.mode]
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium text-text-primary">{response.expertName}</p>
        <div className="flex items-center gap-2">
          <ModeBadge mode={response.mode} />
          <span className="text-xs text-text-secondary">{Math.round(response.confidence * 100)}%</span>
          <button
            type="button"
            onClick={handleDownloadMarkdown}
            aria-label={`Download ${response.expertName} response as Markdown`}
            className="text-xs text-text-secondary hover:text-text-primary"
          >
            Download .md
          </button>
          <button
            type="button"
            onClick={handleCopyResponse}
            aria-label="Copy response"
            className="text-xs text-text-secondary hover:text-text-primary"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {response.warning && (
        <p className="mb-2 rounded-md bg-mode-warn/10 px-2 py-1 text-xs text-mode-warn">
          {response.warning}
        </p>
      )}

      {/* ASK mode - render clarifying questions instead of prose content. */}
      {response.mode === 'ASK' && response.questions && response.questions.length > 0 ? (
        <ul className="list-inside list-disc space-y-1 text-sm text-text-primary">
          {response.questions.map((q) => (
            <li key={q}>{q}</li>
          ))}
        </ul>
      ) : response.templateSections && response.templateSections.length > 0 ? (
        <div className="flex flex-col gap-3">
          {response.templateSections.map((section) => {
            const sectionSegments = splitContentByCitations(section.content, section.citations)
            return (
              <div key={section.key}>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                  {section.label}
                </h4>
                {section.type === 'code' ? (
                  <ReactMarkdown components={{ code: CodeBlock }}>
                    {'```\n' + section.content + '\n```'}
                  </ReactMarkdown>
                ) : (
                  <div className="prose prose-invert prose-sm max-w-none text-text-primary">
                    {sectionSegments.map((segment, i) =>
                      segment.type === 'text' ? (
                        <ReactMarkdown key={i} components={{ code: CodeBlock }}>
                          {segment.value}
                        </ReactMarkdown>
                      ) : (
                        <CitationChip key={i} citation={segment.citation} index={i + 1} />
                      )
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div>
          <div
            className={cn(
              'prose prose-invert prose-sm max-w-none text-text-primary',
              shouldTruncate && 'max-h-[400px] overflow-hidden relative'
            )}
          >
            {segments.map((segment, i) =>
              segment.type === 'text' ? (
                <ReactMarkdown key={i} components={{ code: CodeBlock }}>
                  {segment.value}
                </ReactMarkdown>
              ) : (
                <CitationChip
                  key={i}
                  citation={segment.citation}
                  index={citationIndexMap.get(segment.citation.chunkId) ?? i + 1}
                />
              )
            )}
            {/* Fade overlay when truncated */}
            {shouldTruncate && (
              <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-surface-raised to-transparent pointer-events-none" />
            )}
          </div>
          {/* Show more/less button for long responses */}
          {isLongResponse && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-sm text-brand hover:text-brand-hover transition-colors font-medium"
            >
              {isExpanded ? '▲ Show less' : '▼ Show more'} ({wordCount} words)
            </button>
          )}
        </div>
      )}

      {response.citations && response.citations.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-text-secondary">Citations:</span>
          {response.citations.map((c, i) => (
            <CitationChip key={c.chunkId} citation={c} index={i + 1} />
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-between">
        {persistedMessageId ? (
          <div className="flex items-center gap-3">
            <RatingWidget messageId={persistedMessageId} />
            {/* The explanation sits next to the rating on purpose: both are the
                reader asking "should I trust this?", and the answer is the gates,
                the sources and the claim check. */}
            {/* Deliberately NOT a second "why did I say this": that button
                shows the expert's own decision trace from the stream. This one
                opens the STORED audit record — gates, the sources that were
                actually used, the claim check, the quality verdict and the
                provenance chain — which the stream never carried. */}
            <button
              onClick={() => setShowExplanation(true)}
              className="text-xs text-brand hover:underline"
              title="Gates, sources, claim check, quality and provenance, from the stored record"
            >
              Evidence &amp; audit
            </button>
          </div>
        ) : (
          <span className="text-xs text-text-disabled">
            {isStreaming ? 'Streaming...' : 'Rating available after save'}
          </span>
        )}

        <div className="flex items-center gap-3">
          {/* CT-D5: reply button. Disabled until this response is
              persisted (same gating RatingWidget already uses above). */}
          {chatId && persistedMessageId && (
            <button
              type="button"
              onClick={handleReplyClick}
              className="rounded-md border border-surface-border bg-surface-overlay px-2 py-1 text-xs text-text-secondary hover:text-text-primary hover:border-brand/40 transition-colors"
            >
              Reply
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowReasoning((v) => !v)}
            className={cn(
              'flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-all duration-200',
              showReasoning
                ? 'border-brand/60 bg-brand/10 text-brand shadow-sm'
                : 'border-surface-border bg-surface-overlay text-text-secondary hover:text-text-primary hover:border-brand/40'
            )}
            title="View decision trace and gate-by-gate reasoning"
          >
            <span className="text-sm">{showReasoning ? '\u25bc' : '\u25b6'}</span>
            <span>Why did I say this?</span>
          </button>
        </div>
      </div>

      {showReasoning && (
        <ReasoningPanel gateStopped={response.gateStopped} mode={response.mode} confidence={response.confidence} />
      )}

      {/* The stored audit record, fetched on demand rather than shipped with
          every streamed answer: it is only interesting when someone asks. */}
      {showExplanation && persistedMessageId && (
        <AnswerExplanation messageId={persistedMessageId} onClose={() => setShowExplanation(false)} />
      )}
    </motion.div>
  )
}
